import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { ADD_TO_JOB_ACTION_CANDIDATE_LIMIT } from 'modules/candidate-profile/constants';
import { buildAddToJobActionUrl } from 'modules/candidate-profile/utils/buildAddToJobActionUrl';
import { fetchCandidateProfileExternalIds } from 'modules/candidate-profile/utils/fetchCandidateProfileExternalIds';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';

type UseAddToJobActionProps = {
  selectedCandidateIds: string[];
};

/**
 * Hands the selected candidates to the system the organisation has configured for its
 * add-to-job. That system does the adding; all this does is open it with the candidates
 * named, so there is nothing to confirm and no drawer to fill in.
 */
export const useAddToJobAction = ({
  selectedCandidateIds
}: UseAddToJobActionProps) => {
  const apolloClient = useApolloClient();
  const organizationConfig = useOrganizationConfig();
  const { showToast } = useToast();

  const addToJobActionUrl =
    organizationConfig?.internalSourcingAddToJobActionUrl || null;
  const isAddToJobActionOverLimit =
    selectedCandidateIds.length > ADD_TO_JOB_ACTION_CANDIDATE_LIMIT;

  const handleAddToJobAction = useCallback(async () => {
    if (
      !addToJobActionUrl ||
      selectedCandidateIds.length === 0 ||
      isAddToJobActionOverLimit
    ) {
      return;
    }

    // Popup blockers only trust a window opened during the click itself, so the tab is
    // claimed here and pointed at the URL once the ids come back.
    const actionTab = window.open('', '_blank');

    let externalIds: string[];

    try {
      externalIds = await fetchCandidateProfileExternalIds({
        apolloClient,
        candidateProfileIds: selectedCandidateIds
      });
    } catch {
      // The tab was claimed before the ids were known, so a failed lookup has to close it
      // rather than leave the recruiter looking at a blank one.
      actionTab?.close();
      showToast({
        title:
          'Could not look up the selected candidates, so they were not added to a job',
        type: ToastType.Error
      });
      return;
    }

    // The destination resolves candidates by the customer's own id, so a candidate without
    // one cannot be sent at all.
    if (externalIds.length === 0) {
      actionTab?.close();
      showToast({
        title:
          selectedCandidateIds.length === 1
            ? 'This candidate has no external id, so it cannot be added to a job'
            : 'None of the selected candidates have an external id, so they cannot be added to a job',
        type: ToastType.Error
      });
      return;
    }

    const url = buildAddToJobActionUrl({
      actionUrl: addToJobActionUrl,
      externalIds
    });

    if (!url) {
      actionTab?.close();
      showToast({
        title: 'The add to job link for this organisation is not a valid URL',
        type: ToastType.Error
      });
      return;
    }

    // The rest still go: saying which were left behind beats dropping them silently or
    // refusing the whole selection over one bad row.
    const skippedCount = selectedCandidateIds.length - externalIds.length;
    if (skippedCount > 0) {
      showToast({
        title:
          skippedCount === 1
            ? '1 candidate was left out: no external id'
            : `${skippedCount} candidates were left out: no external id`,
        type: ToastType.Warning
      });
    }

    if (actionTab) {
      actionTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }, [
    addToJobActionUrl,
    selectedCandidateIds,
    isAddToJobActionOverLimit,
    apolloClient,
    showToast
  ]);

  return {
    handleAddToJobAction,
    hasAddToJobAction: Boolean(addToJobActionUrl),
    isAddToJobActionOverLimit
  };
};
